import PropTypes from 'prop-types';
// @mui
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ListItemText from '@mui/material/ListItemText';
import ListItemButton from '@mui/material/ListItemButton';
import CircularProgress from '@mui/material/CircularProgress';
// utils
import { fToNow } from 'src/utils/format-time';
// components
import Iconify from 'src/components/iconify';
import { useRouter } from 'src/routes/hooks';

// ----------------------------------------------------------------------

// Icon + colour per trigger source
const SOURCE_META = {
  leave_application:    { icon: 'solar:calendar-add-bold-duotone',       color: '#FF6B2B', bg: 'rgba(255,107,43,0.12)'  },
  leave_approval:       { icon: 'solar:check-circle-bold-duotone',       color: '#22C55E', bg: 'rgba(34,197,94,0.12)'   },
  leave_rejection:      { icon: 'solar:close-circle-bold-duotone',       color: '#EF4444', bg: 'rgba(239,68,68,0.12)'   },
  leave_cancellation:   { icon: 'solar:calendar-remove-bold-duotone',    color: '#F59E0B', bg: 'rgba(245,158,11,0.12)'  },
  leave_cancel_approved:{ icon: 'solar:check-square-bold-duotone',       color: '#0EA5E9', bg: 'rgba(14,165,233,0.12)'  },
  leave_cancel_rejected:{ icon: 'solar:close-square-bold-duotone',       color: '#DC2626', bg: 'rgba(220,38,38,0.12)'   },
  payroll_published:    { icon: 'solar:wallet-money-bold-duotone',       color: '#8B5CF6', bg: 'rgba(139,92,246,0.12)'  },
  expense_applied:      { icon: 'solar:bill-list-bold-duotone',          color: '#F59E0B', bg: 'rgba(245,158,11,0.12)'  },
  expense_approved:     { icon: 'solar:bill-check-bold-duotone',         color: '#22C55E', bg: 'rgba(34,197,94,0.12)'   },
  expense_rejected:     { icon: 'solar:bill-cross-bold-duotone',         color: '#EF4444', bg: 'rgba(239,68,68,0.12)'   },
  attendance_correction:{ icon: 'solar:clock-circle-bold-duotone',       color: '#0EA5E9', bg: 'rgba(14,165,233,0.12)'  },
  document_request:     { icon: 'solar:document-add-bold-duotone',       color: '#6366F1', bg: 'rgba(99,102,241,0.12)'  },
  manual:               { icon: 'solar:bell-bold-duotone',               color: '#6366F1', bg: 'rgba(99,102,241,0.12)'  },
};


function NotificationAvatar({ triggerSource }) {
  const meta = SOURCE_META[triggerSource] || SOURCE_META.manual;
  return (
    <Avatar
      sx={{
        width: 40,
        height: 40,
        bgcolor: meta.bg,
        flexShrink: 0,
        mr: 1.5,
      }}
    >
      <Iconify icon={meta.icon} width={22} sx={{ color: meta.color }} />
    </Avatar>
  );
}

NotificationAvatar.propTypes = { triggerSource: PropTypes.string };

// ----------------------------------------------------------------------

export default function NotificationItem({
  notification,
  actionLoading,
  onMarkRead,
  onArchive,
  onAccept,
  onReject,
}) {
  const router = useRouter();
  const { id, title, subtitle, message, category, triggerSource, referenceId, actionStatus, createdAt, isUnRead, isArchived } = notification;

  // Show Accept/Reject only if:
  // 1. It's a leave_application with a referenceId AND
  // 2. It has NOT been actioned yet (actionStatus is null or undefined)
  const isLeaveRequest = triggerSource === 'leave_application' && !!referenceId;
  const isAlreadyActioned = !!actionStatus && actionStatus !== 'pending';
  const showActionButtons = isLeaveRequest && !isArchived && !isAlreadyActioned;
  const showStatusBadge  = isLeaveRequest && isAlreadyActioned;

  const getRedirectPath = (source) => {
    switch (source) {
      case 'leave_application':
      case 'leave_cancellation':
        return '/leave_mangement/leave_approvals';
      case 'leave_approval':
      case 'leave_rejection':
      case 'leave_cancel_approved':
      case 'leave_cancel_rejected':
        return '/leave_mangement/leave_applications';
      case 'expense_applied':
        return '/expense/expense_approvals';
      case 'expense_approved':
      case 'expense_rejected':
        return '/expense/manage_expense';
      case 'forget_punch_applied':
        return '/hrm_management/user_management/forget_punch_approval';
      case 'forget_punch_approved':
      case 'forget_punch_rejected':
        return '/hrm_management/forget_punch';
      case 'payroll_published':
        return '/payroll_management/my_payslips';
      default:
        return null;
    }
  };

  const handleClick = () => {
    if (isUnRead) onMarkRead(id);
    const path = getRedirectPath(triggerSource);
    if (path) {
      router.push(path);
    }
  };

  return (
    <ListItemButton
      disableRipple
      onClick={handleClick}
      sx={{
        p: 2,
        alignItems: 'flex-start',
        borderBottom: (theme) => `dashed 1px ${theme.palette.divider}`,
        bgcolor: isUnRead ? 'action.selected' : 'transparent',
        transition: 'background-color 0.2s',
        '&:hover': { bgcolor: 'action.hover' },
        position: 'relative',
      }}
    >
      {/* Unread blue dot */}
      {isUnRead && (
        <Box
          sx={{
            top: 22,
            right: 16,
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: 'info.main',
            position: 'absolute',
            flexShrink: 0,
          }}
        />
      )}

      {/* Avatar */}
      <NotificationAvatar triggerSource={triggerSource} />

      {/* Content */}
      <Box sx={{ flexGrow: 1, minWidth: 0, pr: isUnRead ? 2 : 0 }}>
        <ListItemText
          disableTypography
          primary={
            <Typography
              variant="subtitle2"
              sx={{
                mb: 0.25,
                lineHeight: 1.4,
                fontWeight: isUnRead ? 700 : 500,
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {title}
            </Typography>
          }
          secondary={
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.25 }}>
              <Typography variant="caption" color="text.disabled" noWrap>
                {fToNow(createdAt)}
              </Typography>
              <Box sx={{ width: 2, height: 2, borderRadius: '50%', bgcolor: 'text.disabled', flexShrink: 0 }} />
              <Typography
                variant="caption"
                noWrap
                sx={{
                  color: {
                    leave_application:    'warning.main',
                    leave_approval:       'success.main',
                    leave_rejection:      'error.main',
                    leave_cancellation:   'warning.main',
                    leave_cancel_approved:'info.main',
                    leave_cancel_rejected:'error.main',
                    payroll_published:    'secondary.main',
                  }[triggerSource] || 'text.disabled',
                  fontWeight: 600,
                }}
              >
                {category}
              </Typography>
            </Stack>
          }
        />

        {/* Subtitle */}
        {subtitle && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 0.5,
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 1,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {subtitle}
          </Typography>
        )}

        {/* Message preview box */}
        {message && (
          <Box
            sx={{
              mt: 1,
              p: 1.25,
              borderRadius: 1.5,
              bgcolor: 'background.neutral',
              border: (theme) => `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                whiteSpace: 'pre-line',
                lineHeight: 1.7,
                display: '-webkit-box',
                WebkitLineClamp: 4,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {message}
            </Typography>
          </Box>
        )}

        {/* Leave Request actions — Accept / Decline */}
        {showActionButtons && (
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
            <Button
              size="small"
              variant="contained"
              color="success"
              disabled={actionLoading}
              startIcon={actionLoading
                ? <CircularProgress size={14} color="inherit" />
                : <Iconify icon="eva:checkmark-fill" width={14} />
              }
              onClick={(e) => { e.stopPropagation(); onAccept(notification); }}
              sx={{ flex: 1, fontWeight: 600 }}
            >
              Accept
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              disabled={actionLoading}
              startIcon={<Iconify icon="eva:close-fill" width={14} />}
              onClick={(e) => { e.stopPropagation(); onReject(notification); }}
              sx={{ flex: 1, fontWeight: 600 }}
            >
              Decline
            </Button>
          </Stack>
        )}

        {/* Status badge when already actioned from any page */}
        {showStatusBadge && (
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 1.25 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1.25,
                py: 0.4,
                borderRadius: 1,
                bgcolor: actionStatus === 'approved' ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.10)',
                border: '1px solid',
                borderColor: actionStatus === 'approved' ? 'rgba(34,197,94,0.35)' : 'rgba(239,68,68,0.30)',
              }}
            >
              <Iconify
                icon={actionStatus === 'approved' ? 'eva:checkmark-circle-2-fill' : 'eva:close-circle-fill'}
                width={14}
                sx={{ color: actionStatus === 'approved' ? 'success.main' : 'error.main' }}
              />
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: actionStatus === 'approved' ? 'success.main' : 'error.main',
                  lineHeight: 1,
                }}
              >
                {actionStatus === 'approved' ? 'Approved' : 'Rejected'}
              </Typography>
            </Box>
          </Stack>
        )}

        {/* Archive button for non-leave or already-acted items */}
        {!isLeaveRequest && !isArchived && (
          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
            <Tooltip title="Dismiss">
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); onArchive(id); }}
                sx={{ color: 'text.disabled' }}
              >
                <Iconify icon="eva:close-fill" width={16} />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </Box>
    </ListItemButton>
  );
}

NotificationItem.propTypes = {
  notification:  PropTypes.object.isRequired,
  actionLoading: PropTypes.bool,
  onMarkRead:    PropTypes.func.isRequired,
  onArchive:     PropTypes.func.isRequired,
  onAccept:      PropTypes.func.isRequired,
  onReject:      PropTypes.func.isRequired,
};
